// ReconcilePeekBody — the reconcile half of the ONE site peek (ADR-056 §6),
// judged in Workbench/Solution reconciliation: (1) ONE worst-first observation
// card list (ADR-061 §5 — the grammar judged on the asset register: every live
// finding a card with its fix on it, every ignored one a muted restorable card
// at the foot, never dismiss-and-forget), (2) the current configuration,
// (3) History (periods named by start date only — "Current — since {date}";
// every row carries the verbs). What stays sites-specific is what the domain
// forces: a solution value is a DATED PERIOD, so a conflict's fix is the
// changed-vs-corrected fork and a blank fills through the form — never inline.
// Storage is half-open [from, to); the DISPLAYED end of a closed period is
// to − 1 day. The host owns the ContextPanel, the acts (the site's registry —
// "Update config" is its chooser) and the overlays; this body emits intents.

import { useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';

import { DetailSection } from './DetailSection.js';
import { DescriptionList, Field } from './DescriptionList.js';
import { ObservationCard } from './observations.js';
import { Button } from '../ui/Button.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { DatePicker } from '../ui/DatePicker.js';
import { IconButton } from '../ui/IconButton.js';
import { Menu, MenuItem, MenuSeparator } from '../ui/Menu.js';
import { icons } from '../ui/icons.js';
import { formatDate, formatNumber } from '../../lib/format.js';
import type { WeekStart } from '@ems-app/schemas/common/period';
import { todayIn } from '../../lib/period.js';
import { familyDef } from '@ems-app/schemas/observations/registry';
import type { DiscoverableConfigField } from '@ems-app/schemas/sites/discoveries';
import type { SiteStatus } from '@ems-app/schemas/sites/requests';
import type { SiteSuggestion, SolutionConfig } from '@ems-app/schemas/sites/responses';
import {
  CONFIG_FIELD_META,
  declaredDisplay,
  discoveredDisplay,
  solutionPeekObservations,
  toConfigValues,
  type ConfigValues,
  type SiteObservation,
} from './solutionConfig.js';

function shiftDay(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** A closed period's displayed (inclusive) end — storage is half-open. */
export function displayEnd(effectiveTo: string): string {
  return shiftDay(effectiveTo, -1);
}

/** The date fields every dated-period timeline shares — the solution SCD and the
 *  designation SCD (ADR-060 §3) render their date grammar through the same helpers;
 *  the per-timeline COPY differs, so the key prefix is a parameter. */
export interface TimelinePeriod {
  id: number;
  effective_from: string;
  effective_to: string | null;
}

/** The delete confirm's consequence, in ONE plain sentence per case (ADR-056 §6).
 *  `keys` = the caller's copy home (default = the solution timeline's). */
export function deleteConsequence(history: TimelinePeriod[], periodId: number, t: TFunction, keys = 'reconcile.delete'): string {
  const asc = [...history].sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1));
  const idx = asc.findIndex((p) => p.id === periodId);
  const target = asc[idx];
  if (!target) return '';
  if (asc.length === 1) return t(`${keys}.only`);
  if (idx === 0) return t(`${keys}.first`, { next: formatDate(asc[1]!.effective_from, 'date') });
  const prev = formatDate(asc[idx - 1]!.effective_from, 'date');
  if (target.effective_to === null) return t(`${keys}.current`, { prev });
  return t(`${keys}.middle`, { prev, end: formatDate(displayEnd(target.effective_to), 'date') });
}

export interface ReconcilePeekBodyProps {
  /** The site's lifecycle status — draft and decommissioned raise nothing. */
  status: SiteStatus;
  /** The site's zone — the one zone of the peek it sits in (ADR-082 §2). */
  timeZone?: string;
  /** Open + ignored suggestions (the detail payload). */
  suggestions: SiteSuggestion[];
  /** The current period's values — null = Not configured. */
  config: ConfigValues | null;
  /** The full timeline, newest first (the detail payload's solution_history). */
  history: SolutionConfig[];
  /** Verbs hidden without site.solution_config:write (reads stay open). */
  canWrite: boolean;
  /** Open the ONE config form: changed = new period; correct targets a period id. */
  onOpenForm: (mode: 'changed' | 'correct', periodId: number | null, focusField?: DiscoverableConfigField) => void;
  onStartDate: (periodId: number) => void;
  onDelete: (periodId: number) => void;
  /** Per-field dismiss (→ a muted card at the foot, restorable — so no confirm). */
  onDismiss: (field: string) => void;
  onRestore: (field: string) => void;
  /** An active site must keep a config — its only period's delete is blocked. */
  deleteBlocked: boolean;
  t: TFunction;
}

export function ReconcilePeekBody({
  status,
  suggestions,
  config,
  history,
  canWrite,
  onOpenForm,
  onStartDate,
  onDelete,
  onDismiss,
  onRestore,
  deleteBlocked,
  timeZone,
  t,
}: ReconcilePeekBodyProps) {
  const live = solutionPeekObservations(status, config, suggestions);
  const ignored = suggestions.filter((s) => s.status === 'ignored');
  const current = history.find((p) => p.is_current) ?? null;

  // Per-card actions — the family's permitted reactions (ADR-061 §6): missing
  // gets Fill in / Set up and never a Dismiss; the conflict keeps the
  // changed-vs-corrected fork because a solution value is a dated period.
  const cardActions = (o: SiteObservation): ReactNode => {
    if (!canWrite) return undefined;
    if (o.family === 'site.no_config') {
      return (
        <Button variant="primary" onClick={() => onOpenForm('changed', null)}>
          {t('reconcile.verbs.configure')}
        </Button>
      );
    }
    if (o.kind === 'missing') {
      return (
        <Button variant="primary" onClick={() => current && onOpenForm('correct', current.id, o.field)}>
          {t('reconcile.verbs.fill')}
        </Button>
      );
    }
    return (
      <>
        <Menu
          label={t('reconcile.verbs.update')}
          renderTrigger={() => (
            <Button variant="primary">
              {t('reconcile.verbs.update')} <icons.caretDown size={14} aria-hidden />
            </Button>
          )}
        >
          <MenuItem onAction={() => onOpenForm('changed', null, o.field)}>
            <MenuOptionLabel label={t('reconcile.menu.changed')} help={t('reconcile.menu.changedHelp')} />
          </MenuItem>
          <MenuItem onAction={() => current && onOpenForm('correct', current.id, o.field)}>
            <MenuOptionLabel label={t('reconcile.menu.correctData')} help={t('reconcile.menu.correctDataHelp')} />
          </MenuItem>
        </Menu>
        <Button variant="secondary" onClick={() => o.field && onDismiss(o.field)}>
          {t('reconcile.verbs.dismiss')}
        </Button>
      </>
    );
  };

  const comparePair = (field: DiscoverableConfigField, discovered: string | null, discoveredAt: string | null | undefined) => ({
    declaredLabel: t('reconcile.peek.declared'),
    declared: declaredDisplay(field, config, t),
    foundLabel: t('reconcile.peek.discovered'),
    found: discoveredDisplay(field, discovered, t),
    vsLabel: t('observations.vs'),
    provenance: discoveredAt ? t('reconcile.peek.discoveredOn', { date: formatDate(discoveredAt, 'date', timeZone ? { timeZone } : {}) }) : undefined,
  });

  return (
    <div className="flex flex-col gap-3">
      {/* 1. ONE worst-first card list — every live finding a card with its fix on
          it; the ignored ones rest at the foot as muted restorable cards. */}
      {(live.length > 0 || ignored.length > 0) && (
        <DetailSection dense title={t('observations.lens')}>
          <div className="flex flex-col gap-2">
            {live.map((o) => (
              <ObservationCard
                key={`${o.family}:${o.field ?? ''}`}
                kind={o.kind}
                impact={o.impact}
                kindLabel={t(`observations.kind.${o.kind}`)}
                subject={o.field ? t(CONFIG_FIELD_META[o.field]?.labelKey ?? o.field) : t(`observations.family.${o.family}`)}
                compare={o.suggestion ? comparePair(o.field!, o.suggestion.discovered, o.suggestion.discovered_at) : undefined}
                actions={cardActions(o)}
              />
            ))}
            {ignored.map((s) => (
              <ObservationCard
                key={`ignored:${s.field}`}
                kind="conflict"
                impact={familyDef('site.discovered_differs').impact}
                kindLabel={t('observations.kind.conflict')}
                subject={t(CONFIG_FIELD_META[s.field as DiscoverableConfigField]?.labelKey ?? s.field)}
                compare={comparePair(s.field as DiscoverableConfigField, s.discovered, s.discovered_at)}
                dismissed={{
                  line: s.ignored_at ? t('reconcile.peek.ignoredOn', { date: formatDate(s.ignored_at, 'date', timeZone ? { timeZone } : {}) }) : '',
                  action: canWrite ? (
                    <Button variant="secondary" onClick={() => onRestore(s.field)}>{t('reconcile.verbs.restore')}</Button>
                  ) : undefined,
                }}
              />
            ))}
          </div>
        </DetailSection>
      )}

      {/* 2. The current configuration at a glance. */}
      {config && (
        <DetailSection dense title={t('reconcile.peek.config')}>
          <DescriptionList>
            {config.has_grid && (
              <Field label={t('filters.grid')}>
                <span dir="ltr">
                  {[config.grid_provider, config.grid_capacity_kw != null ? `${formatNumber(config.grid_capacity_kw)} kW` : null]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </span>
              </Field>
            )}
            {config.has_solar && (
              <Field label={t('filters.solar')}>
                <span dir="ltr">{config.solar_capacity_kwp != null ? `${formatNumber(config.solar_capacity_kwp)} kWp` : '—'}</span>
              </Field>
            )}
            {config.has_generator && (
              <Field label={t('filters.generator')}>
                <span dir="ltr">
                  {[
                    config.generator_capacity_kva != null ? `${formatNumber(config.generator_capacity_kva)} kVA` : null,
                    config.generator_units != null ? t('solution.units', { count: config.generator_units }) : null,
                    config.generator_tank_l != null ? t('solution.tank', { l: `${formatNumber(config.generator_tank_l)} L` }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </span>
              </Field>
            )}
            {config.has_provider_generator && (
              <Field label={t('config.sections.providerGenerator')}>
                <span dir="ltr">
                  {[config.provider_generator_name, config.provider_generator_auto_failover ? t('solutionType.sla247') : null]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </span>
              </Field>
            )}
            {config.has_wind && (
              <Field label={t('detail.fields.wind')}>
                <span dir="ltr">{config.wind_capacity_kw != null ? `${formatNumber(config.wind_capacity_kw)} kW` : '—'}</span>
              </Field>
            )}
            {config.has_battery && (
              <Field label={t('detail.fields.battery')}>
                <span dir="ltr">
                  {[config.battery_chemistry, config.battery_capacity_ah != null ? `${formatNumber(config.battery_capacity_ah)} Ah` : null]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </span>
              </Field>
            )}
            {config.has_cooling && (
              <Field label={t('detail.fields.cooling')}>
                <span dir="ltr">
                  {[
                    config.cooling_units != null ? t('solution.units', { count: config.cooling_units }) : null,
                    config.cooling_capacity_kw != null ? `${formatNumber(config.cooling_capacity_kw)} kW` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </span>
              </Field>
            )}
            <Field label={t('detail.fields.powerSystem')}>
              <span dir="ltr">{config.power_system_capacity_kw != null ? `${formatNumber(config.power_system_capacity_kw)} kW` : '—'}</span>
            </Field>
          </DescriptionList>
        </DetailSection>
      )}

      {/* 3. History — periods by start date only; every row carries the verbs. */}
      {history.length > 0 && (
        <DetailSection dense title={t('reconcile.peek.history')}>
          <ol className="m-0 flex list-none flex-col gap-2 p-0">
            {history.map((p) => {
              const isCurrent = p.effective_to === null;
              const label = isCurrent
                ? t('reconcile.peek.current', { date: formatDate(p.effective_from, 'date') })
                : t('reconcile.peek.previous', {
                    from: formatDate(p.effective_from, 'date'),
                    to: formatDate(displayEnd(p.effective_to!), 'date'),
                  });
              return (
                <li key={p.id} className="flex items-start justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <span className={isCurrent ? 'font-medium' : ''}>{label}</span>
                    {p.change_reason && (
                      <div className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{p.change_reason}</div>
                    )}
                  </div>
                  {canWrite && (
                    <Menu
                      label={t('reconcile.peek.rowActions')}
                      renderTrigger={() => (
                        <IconButton aria-label={t('reconcile.peek.rowActions')}>
                          <icons.moreHorizontal size={16} aria-hidden />
                        </IconButton>
                      )}
                    >
                      <MenuItem icon={<icons.edit size={16} aria-hidden />} onAction={() => onOpenForm('correct', p.id)}>
                        {t('reconcile.menu.correctData')}
                      </MenuItem>
                      <MenuItem icon={<icons.calendar size={16} aria-hidden />} onAction={() => onStartDate(p.id)}>
                        {t('reconcile.menu.correctDate')}
                      </MenuItem>
                      <MenuSeparator />
                      <MenuItem
                        tone="danger"
                        icon={<icons.delete size={16} aria-hidden />}
                        isDisabled={isCurrent && deleteBlocked}
                        onAction={() => onDelete(p.id)}
                      >
                        {t('reconcile.delete.confirm')}
                      </MenuItem>
                    </Menu>
                  )}
                </li>
              );
            })}
          </ol>
        </DetailSection>
      )}

    </div>
  );
}

/** A menu option with its one-line consequence (the body's "Update ▾" on a conflict card). */
function MenuOptionLabel({ label, help }: { label: string; help: string }) {
  return (
    <div className="min-w-0 py-0.5">
      <div>{label}</div>
      <div className="text-xs text-[var(--color-text-secondary)]">{help}</div>
    </div>
  );
}

/** "Correct start date" — one date, the consequence in one plain sentence. The
 *  boundary moves only between its neighbours (half-open bounds); the earliest
 *  one is free to extend known history backward. Timeline-agnostic (dates only);
 *  `keys` = the caller's copy home (default = the solution timeline's). */
export function StartDateDialog({
  history,
  periodId,
  onSubmit,
  onClose,
  t,
  keys = 'reconcile.startDate',
  timeZone,
  weekStart,
}: {
  history: TimelinePeriod[];
  periodId: number;
  /** The site's zone — "today", the latest allowed start, is the site's day (ADR-082 §6), never the machine's. */
  timeZone?: string;
  /** The project's week start — the calendar grid's first column (ADR-082 §4). */
  weekStart?: WeekStart;
  /** The save — awaited: the dialog closes on success and says a refusal itself (the confirm's own line). */
  onSubmit: (effectiveFrom: string) => Promise<unknown>;
  onClose: () => void;
  t: TFunction;
  keys?: string;
}) {
  const asc = [...history].sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1));
  const idx = asc.findIndex((p) => p.id === periodId);
  const target = asc[idx]!;
  const prev = idx > 0 ? asc[idx - 1]! : null;
  const [value, setValue] = useState<string | null>(target.effective_from);

  const today = todayIn(timeZone ?? 'UTC');
  const minValue = prev ? shiftDay(prev.effective_from, 1) : undefined;
  const maxValue = target.effective_to === null ? today : shiftDay(target.effective_to, -1);

  const consequence =
    value === null
      ? ''
      : prev
        ? t(`${keys}.consequence`, { date: formatDate(shiftDay(value, -1), 'date') })
        : t(`${keys}.consequenceFirst`, { date: formatDate(value, 'date') });

  // The allowed window, stated up-front (so it isn't discovered on save): the
  // earliest period is open-ended backward (up to its end); any later one is
  // bounded by its predecessor's start and its own end. minValue is defined
  // exactly when a predecessor exists.
  const rangeHelp = prev
    ? t(`${keys}.rangeMid`, { min: formatDate(minValue!, 'date'), max: formatDate(maxValue, 'date') })
    : t(`${keys}.rangeFirst`, { max: formatDate(maxValue, 'date') });

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      title={t(`${keys}.title`)}
      confirmLabel={t(`${keys}.submit`)}
      tone="default"
      onConfirm={async () => {
        if (value) await onSubmit(value);
      }}
    >
      <div className="flex flex-col gap-2">
        <DatePicker label={t('reconcile.form.sinceWhen')} value={value} onChange={setValue} minValue={minValue} maxValue={maxValue} weekStart={weekStart} zone={timeZone} focusOnMount />
        <p className="text-xs text-[var(--color-text-secondary)]">{rangeHelp}</p>
        {consequence && <p className="text-xs text-[var(--color-text-secondary)]">{consequence}</p>}
      </div>
    </ConfirmDialog>
  );
}

export { toConfigValues };
